import { Link } from "react-router-dom";
import {
  LineChart, Factory, ClipboardList, Ship, Building2, Hotel, HardHat,
  PencilRuler, Boxes, CheckCircle2, Anchor, ArrowRight, UploadCloud,
  SlidersHorizontal, Calculator, Search, Scale, Truck, PackageCheck,
  Radar, Database, Sparkles,
} from "lucide-react";
import Reveal from "../components/Reveal";
import { Flow, Eyebrow, PrimaryBtn, GhostBtn } from "../components/Flow";

const IMG = {
  hero: "https://images.unsplash.com/photo-1541447271487-09612b3f49f7?auto=format&fit=crop&w=2000&q=75",
  resort: "https://images.unsplash.com/photo-1649147391870-a170da925530?auto=format&fit=crop&w=1400&q=70",
  port: "https://images.unsplash.com/photo-1578575437130-527eed3abbec?auto=format&fit=crop&w=1600&q=70",
  containers: "https://images.unsplash.com/photo-1494412574643-ff11b0a5c1c3?auto=format&fit=crop&w=1400&q=70",
  construction: "https://images.unsplash.com/photo-1541888946425-d81bb19240f5?auto=format&fit=crop&w=1400&q=70",
  villa: "https://images.unsplash.com/photo-1613490493576-7fde63acd811?auto=format&fit=crop&w=1400&q=70",
};

const PILLARS = [
  {
    icon: LineChart,
    num: "01",
    title: "REAL ESTATE INTELLIGENCE",
    desc: "Project analysis, budget scenarios, product specification and procurement strategy — before you commit.",
    items: ["Project analysis", "Construction budget analysis", "Product specification", "Quantity estimation", "Cost scenarios", "Procurement strategy", "Project optimization"],
    cta: "Explore Project Intelligence",
    testid: "pillar-intelligence-card",
  },
  {
    icon: Factory,
    num: "02",
    title: "COLOMBIAN SOURCING",
    desc: "We connect your project with selected Colombian manufacturers and suppliers across every finish category.",
    items: ["Ceramics", "Porcelain", "Stone", "Sanitaryware", "Faucets", "Doors", "Windows", "Aluminium", "Glass", "Kitchens", "Furniture", "Lighting", "Paint", "Waterproofing", "Prefabricated solutions"],
    cta: "Explore Sourcing",
    testid: "pillar-sourcing-card",
  },
  {
    icon: ClipboardList,
    num: "03",
    title: "PROCUREMENT & EXPORT",
    desc: "From multiple Colombian suppliers to one coordinated procurement process.",
    items: [],
    flow: ["Project requirements", "Supplier search", "Quotation", "Negotiation", "Quality / Documentation", "Consolidation", "Export", "Caribbean delivery"],
    cta: "Explore Procurement",
    testid: "pillar-procurement-card",
  },
  {
    icon: Ship,
    num: "04",
    title: "LOGISTICS & PROJECT SUPPORT",
    desc: "We coordinate the commercial and logistical process between Colombian suppliers and your Caribbean project.",
    items: ["Supplier coordination", "Consolidation", "Export documentation", "International freight coordination", "Shipment tracking", "Delivery coordination", "Project follow-up"],
    note: "Coordination with qualified logistics and local partners when required.",
    cta: "Explore Logistics",
    testid: "pillar-logistics-card",
  },
];

const CLIENTS = [
  { icon: Building2, title: "REAL ESTATE DEVELOPERS", desc: "Residential developments, condominiums and mixed-use projects.", testid: "client-developers-card" },
  { icon: Hotel, title: "HOTEL DEVELOPERS", desc: "Hotels, resorts, boutique hotels and hospitality projects.", testid: "client-hotels-card" },
  { icon: HardHat, title: "CONSTRUCTION COMPANIES", desc: "Reliable sourcing and procurement for construction projects.", testid: "client-construction-card" },
  { icon: PencilRuler, title: "ARCHITECTS & DESIGNERS", desc: "Product discovery, specification and Colombian sourcing.", testid: "client-architects-card" },
  { icon: Boxes, title: "DISTRIBUTORS & IMPORTERS", desc: "Access to selected Colombian manufacturers and product categories.", testid: "client-distributors-card" },
];

const PACKAGES = [
  { name: "ESSENTIAL", target: "For cost-efficient residential projects.", focus: ["Value", "Reliability", "Standardization", "Optimized procurement"], testid: "package-essential-card" },
  { name: "PREMIUM", target: "For mid/high-end residential developments.", focus: ["Design", "Quality", "Customization", "Performance"], featured: true, testid: "package-premium-card" },
  { name: "CARIBBEAN LUXURY", target: "Villas, resorts, luxury residences and boutique hotels.", focus: ["Premium materials", "Design", "Custom finishes", "Hospitality experience"], testid: "package-luxury-card" },
];

const STEPS = [
  { n: "01", t: "SHARE YOUR PROJECT", d: "Send plans, specifications, quantities or simply describe your project." },
  { n: "02", t: "PROJECT ANALYSIS", d: "We identify your procurement requirements." },
  { n: "03", t: "PRODUCT SELECTION", d: "We identify suitable Colombian products and suppliers." },
  { n: "04", t: "SOURCING & NEGOTIATION", d: "We request quotations and compare solutions." },
  { n: "05", t: "PROCUREMENT", d: "We coordinate orders with selected suppliers." },
  { n: "06", t: "CONSOLIDATION & EXPORT", d: "We coordinate the logistics process from Colombia." },
  { n: "07", t: "CARIBBEAN DELIVERY", d: "We coordinate the final logistics process with qualified local partners where required." },
];

const WHY_COLOMBIA = [
  "Strong manufacturing base",
  "Competitive product categories",
  "Proximity to Caribbean markets",
  "Established export infrastructure",
  "Design and manufacturing capabilities",
  "Broad range of construction and finishing products",
  "Strong Caribbean commercial connections",
];

const FUTURE_FEATURES = [
  { icon: UploadCloud, t: "Upload project plans" },
  { icon: SlidersHorizontal, t: "Define project size & quality level" },
  { icon: Calculator, t: "Estimate quantities" },
  { icon: Search, t: "Discover Colombian products" },
  { icon: Scale, t: "Compare suppliers" },
  { icon: Truck, t: "Estimate logistics" },
  { icon: Radar, t: "Track orders" },
  { icon: PackageCheck, t: "Monitor procurement" },
  { icon: Ship, t: "Follow deliveries" },
  { icon: Database, t: "Project data, in one place" },
];

const DESTINATIONS = ["Dominican Republic", "Aruba", "Curaçao", "Jamaica", "Bahamas", "Trinidad & Tobago", "Puerto Rico"];

export default function Home() {
  return (
    <main data-testid="home-page">
      {/* 1 — HERO */}
      <section className="relative flex min-h-[92vh] items-center overflow-hidden bg-[#0B132B]" data-testid="hero-section">
        <img src={IMG.hero} alt="Modern Caribbean architectural development" className="hero-img absolute inset-0 h-full w-full object-cover opacity-45" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B132B] via-[#0B132B]/85 to-[#0B132B]/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B132B] via-transparent to-transparent" />
        <div className="relative mx-auto w-full max-w-7xl px-5 py-28 lg:px-8">
          <Reveal>
            <Eyebrow dark>Colombia → Caribbean · Construction Procurement Partner</Eyebrow>
            <h1 className="mt-6 max-w-4xl font-display text-4xl font-extrabold leading-[0.98] tracking-[-0.03em] text-white sm:text-5xl lg:text-6xl" data-testid="hero-headline">
              SOURCE IN COLOMBIA.<br />BUILD IN THE CARIBBEAN.
            </h1>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-white/70 md:text-lg" data-testid="hero-subheadline">
              BUY CARIBBEAN connects Caribbean developers, builders and hospitality projects with selected Colombian products, manufacturers and procurement solutions.
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-4">
              <PrimaryBtn to="/contact" testid="hero-cta-submit-project" light>SUBMIT YOUR PROJECT</PrimaryBtn>
              <GhostBtn to="/solutions" testid="hero-cta-explore-solutions" dark>EXPLORE OUR SOLUTIONS</GhostBtn>
            </div>
          </Reveal>
          <Reveal delay={200} className="mt-16 border-t border-white/15 pt-7">
            <Flow
              dark
              testid="hero-corridor-flow"
              items={["Colombia", "Sourcing", "Procurement", "Logistics", "Caribbean Project"]}
            />
          </Reveal>
        </div>
      </section>

      {/* 2 — THE PROBLEM */}
      <section className="py-20 sm:py-28" data-testid="problem-section">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="grid gap-14 lg:grid-cols-2 lg:items-start">
            <Reveal>
              <Eyebrow>The Problem</Eyebrow>
              <h2 className="mt-5 font-display text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl" data-testid="problem-headline">
                Building in the Caribbean should not mean managing dozens of suppliers.
              </h2>
              <p className="mt-6 max-w-xl text-sm leading-relaxed text-[#0B132B]/70 sm:text-base">
                A Caribbean construction or hospitality project may require flooring, ceramics, sanitaryware, faucets, doors, windows, aluminium, kitchens, lighting, furniture, construction materials and exterior products — each from a different supplier, each with its own process.
              </p>
              <div className="img-zoom mt-8 overflow-hidden">
                <img src={IMG.construction} alt="Construction detail on a Caribbean project" className="aspect-[16/9] w-full object-cover" />
              </div>
            </Reveal>
            <Reveal delay={150}>
              <div className="border border-[#0B132B]/12 bg-white p-7 sm:p-9">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#0B132B]/45">What buyers manage today</p>
                <ul className="mt-6 grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2" data-testid="problem-burden-list">
                  {["Supplier identification", "Negotiations", "Product selection", "Specifications", "Quantities", "Quality", "Documentation", "Consolidation", "Shipping", "Customs", "Delivery", "Project deadlines"].map((b) => (
                    <li key={b} className="flex items-center gap-2.5 text-sm text-[#0B132B]/80">
                      <span className="h-1.5 w-1.5 shrink-0 bg-[#D97706]" />
                      {b}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* 3 — THE SOLUTION */}
      <section className="bg-[#0B132B] py-20 text-white sm:py-24" data-testid="solution-section">
        <div className="mx-auto max-w-7xl px-5 text-center lg:px-8">
          <Reveal>
            <Eyebrow dark>The Solution</Eyebrow>
            <h2 className="mx-auto mt-5 max-w-3xl font-display text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl" data-testid="solution-headline">
              One partner. Multiple suppliers. One coordinated procurement process.
            </h2>
            <p className="mx-auto mt-6 max-w-2xl text-sm leading-relaxed text-white/65 sm:text-base">
              Your Caribbean project. One procurement partner. From project intelligence to Colombian sourcing and Caribbean delivery.
            </p>
          </Reveal>
        </div>
      </section>

      {/* 4 — FOUR PILLARS */}
      <section className="py-20 sm:py-28" data-testid="pillars-section">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal className="max-w-2xl">
            <Eyebrow>What We Do</Eyebrow>
            <h2 className="mt-5 font-display text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">
              Four pillars. One supply strategy.
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-6 md:grid-cols-2">
            {PILLARS.map((p, i) => (
              <Reveal key={p.title} delay={i * 100}>
                <div className="card-lift flex h-full flex-col border border-[#0B132B]/12 bg-white p-7 sm:p-9" data-testid={p.testid}>
                  <div className="flex items-start justify-between">
                    <p.icon className="h-7 w-7 text-[#0D9488]" strokeWidth={1.6} />
                    <span className="font-display text-sm font-bold text-[#0B132B]/25">{p.num}</span>
                  </div>
                  <h3 className="mt-6 font-display text-xl font-bold tracking-tight sm:text-2xl">{p.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-[#0B132B]/70">{p.desc}</p>
                  {p.items.length > 0 && (
                    <div className="mt-5 flex flex-wrap gap-2">
                      {p.items.map((it) => (
                        <span key={it} className="rounded-full bg-[#0B132B]/[0.05] px-3 py-1 text-[11px] font-semibold text-[#0B132B]/70">{it}</span>
                      ))}
                    </div>
                  )}
                  {p.flow && <div className="mt-5"><Flow vertical items={p.flow} testid="procurement-sequence" /></div>}
                  {p.note && <p className="mt-4 text-xs italic text-[#0B132B]/50">{p.note}</p>}
                  <Link to="/solutions" data-testid={`${p.testid}-cta`} className="mt-auto inline-flex items-center gap-2 pt-6 text-sm font-bold text-[#0D9488] transition-colors hover:text-[#0B132B]">
                    {p.cta} <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 5 — CLIENTS */}
      <section className="border-y border-[#0B132B]/10 bg-white py-20 sm:py-24" data-testid="clients-section">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal className="max-w-2xl">
            <Eyebrow>Who We Serve</Eyebrow>
            <h2 className="mt-5 font-display text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">
              Built for the professionals behind Caribbean projects.
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-5">
            {CLIENTS.map((c, i) => (
              <Reveal key={c.title} delay={i * 80}>
                <div className="card-lift h-full border border-[#0B132B]/12 bg-[#F8F7F4] p-6" data-testid={c.testid}>
                  <c.icon className="h-6 w-6 text-[#0D9488]" strokeWidth={1.6} />
                  <h3 className="mt-5 font-display text-sm font-bold tracking-wide">{c.title}</h3>
                  <p className="mt-2.5 text-[13px] leading-relaxed text-[#0B132B]/65">{c.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 6 — PROJECT SOLUTIONS PACKAGES */}
      <section className="py-20 sm:py-28" data-testid="packages-section">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal className="max-w-2xl">
            <Eyebrow>Project Solutions</Eyebrow>
            <h2 className="mt-5 font-display text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">
              Three ways to structure your supply.
            </h2>
            <p className="mt-4 text-sm text-[#0B132B]/65 sm:text-base">
              Conceptual procurement packages — scoped and priced per project, never off-the-shelf.
            </p>
          </Reveal>
          <div className="mt-12 grid gap-6 lg:grid-cols-3">
            {PACKAGES.map((p, i) => (
              <Reveal key={p.name} delay={i * 100}>
                <div
                  className={`card-lift flex h-full flex-col p-8 sm:p-10 ${
                    p.featured
                      ? "border-2 border-[#0D9488] bg-white"
                      : "border border-[#0B132B]/12 bg-white"
                  }`}
                  data-testid={p.testid}
                >
                  {p.featured && (
                    <span className="mb-4 w-fit rounded-full bg-[#0D9488] px-3 py-1 text-[10px] font-bold uppercase tracking-[0.15em] text-white">Most requested</span>
                  )}
                  <h3 className="font-display text-xl font-bold tracking-wide sm:text-2xl">{p.name}</h3>
                  <p className="mt-2 text-sm text-[#0B132B]/65">{p.target}</p>
                  <ul className="mt-6 space-y-2.5">
                    {p.focus.map((f) => (
                      <li key={f} className="flex items-center gap-2.5 text-sm text-[#0B132B]/80">
                        <CheckCircle2 className="h-4 w-4 text-[#0D9488]" /> {f}
                      </li>
                    ))}
                  </ul>
                  <Link to="/contact" data-testid={`${p.testid}-cta`} className="mt-auto inline-flex items-center gap-2 pt-8 text-sm font-bold text-[#0D9488] transition-colors hover:text-[#0B132B]">
                    DISCUSS YOUR PROJECT <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 7 — HOTEL PROCUREMENT */}
      <section className="relative overflow-hidden bg-[#0B132B] py-20 text-white sm:py-28 grain-overlay" data-testid="hotel-section">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
            <Reveal>
              <Eyebrow dark>Caribbean Hotel Procurement</Eyebrow>
              <h2 className="mt-5 font-display text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl" data-testid="hotel-headline">
                One hotel. Hundreds of products. One procurement partner.
              </h2>
              <p className="mt-5 text-sm leading-relaxed text-white/65 sm:text-base">
                Guest rooms, bathrooms, flooring, wall finishes, doors, lighting, furniture, kitchens, restaurants, bars, pool areas, outdoor furniture and common areas — sourced and coordinated through a single process.
              </p>
              <div className="mt-8">
                <Flow
                  dark
                  testid="hotel-procurement-map"
                  items={["Hotel Project", "Rooms", "Bathrooms", "Furniture", "Lighting", "Kitchen", "Common Areas", "Outdoor", "Logistics"]}
                />
              </div>
              <div className="mt-9">
                <PrimaryBtn to="/contact" testid="hotel-cta-plan" light>BUILD A HOTEL PROCUREMENT PLAN</PrimaryBtn>
              </div>
            </Reveal>
            <Reveal delay={150}>
              <div className="img-zoom relative">
                <img src={IMG.resort} alt="Premium Caribbean hotel interior" className="aspect-[4/3] w-full object-cover" />
                <div className="absolute inset-0 border border-white/15" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* 8 — HOW IT WORKS */}
      <section className="py-20 sm:py-28" data-testid="process-section">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal className="max-w-2xl">
            <Eyebrow>How It Works</Eyebrow>
            <h2 className="mt-5 font-display text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">
              Seven steps from plans to delivery.
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-px border border-[#0B132B]/12 bg-[#0B132B]/12 sm:grid-cols-2 lg:grid-cols-4">
            {STEPS.map((s, i) => (
              <div key={s.n} className="bg-[#F8F7F4] p-7" data-testid={`process-step-${s.n}`}>
                <Reveal delay={i * 60}>
                  <span className="font-display text-3xl font-extrabold text-[#0D9488]/30">{s.n}</span>
                  <h3 className="mt-3 font-display text-sm font-bold tracking-wide">{s.t}</h3>
                  <p className="mt-2 text-[13px] leading-relaxed text-[#0B132B]/65">{s.d}</p>
                </Reveal>
              </div>
            ))}
            <div className="flex items-center justify-center bg-[#0B132B] p-7">
              <Link to="/contact" data-testid="process-cta-start" className="group inline-flex items-center gap-2 font-display text-sm font-bold tracking-wide text-white">
                START YOUR PROJECT
                <ArrowRight className="h-4 w-4 text-[#14B8A6] transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* 9 — FUTURE TECH DASHBOARD */}
      <section className="relative overflow-hidden bg-[#0B132B] py-20 text-white sm:py-28 grain-overlay" data-testid="tech-section">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="grid gap-14 lg:grid-cols-2 lg:items-center">
            <Reveal>
              <Eyebrow dark>Technology Roadmap</Eyebrow>
              <h2 className="mt-5 font-display text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl" data-testid="tech-headline">
                FROM PROCUREMENT TO CONSTRUCTION INTELLIGENCE
              </h2>
              <p className="mt-5 text-sm leading-relaxed text-white/65 sm:text-base">
                BUY CARIBBEAN is building a technology-enabled approach to construction procurement.
              </p>
              <p className="mt-6 font-display text-lg font-semibold text-[#14B8A6]">
                The future: a digital procurement layer for Caribbean construction projects.
              </p>
              <div className="mt-8">
                <Flow dark testid="tech-vision-flow" items={["Project", "Plans", "Quantities", "Products", "Suppliers", "Orders", "Containers", "Delivery", "Budget", "Monitoring"]} />
              </div>
            </Reveal>
            <Reveal delay={150}>
              <div className="relative border border-white/15 bg-[#131C35] p-6 shadow-[0_0_90px_-25px_rgba(20,184,166,0.45)] sm:p-8" data-testid="tech-dashboard-mockup">
                <span className="absolute -top-3 right-5 bg-[#D97706] px-3 py-1 text-[10px] font-bold uppercase tracking-[0.15em] text-[#0B132B]">
                  Concept / Future Platform
                </span>
                <div className="flex items-center gap-2">
                  <span className="h-2.5 w-2.5 rounded-full bg-white/20" />
                  <span className="h-2.5 w-2.5 rounded-full bg-white/20" />
                  <span className="h-2.5 w-2.5 rounded-full bg-[#14B8A6]" />
                  <span className="ml-3 text-[11px] font-bold uppercase tracking-[0.18em] text-white/50">BUY CARIBBEAN · Project Command</span>
                </div>
                <div className="mt-6 grid grid-cols-2 gap-4">
                  <div className="col-span-2 border border-white/10 bg-white/[0.04] p-4">
                    <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/40">Project</p>
                    <p className="mt-1 font-display text-lg font-bold">Caribbean Residence — 120 Units</p>
                  </div>
                  <div className="border border-white/10 bg-white/[0.04] p-4">
                    <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/40">Material Budget</p>
                    <p className="mt-1 font-display text-xl font-bold text-[#14B8A6]">$X,XXX,XXX</p>
                  </div>
                  <div className="border border-white/10 bg-white/[0.04] p-4">
                    <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/40">Budget</p>
                    <p className="mt-1 font-display text-xl font-bold text-[#14B8A6]">ON TRACK</p>
                  </div>
                  <div className="border border-white/10 bg-white/[0.04] p-4">
                    <div className="flex items-center justify-between">
                      <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/40">Ordered</p>
                      <p className="font-display text-sm font-bold">XX%</p>
                    </div>
                    <div className="mt-2 h-1.5 bg-white/10"><div className="h-full w-[78%] bg-[#14B8A6]" /></div>
                  </div>
                  <div className="border border-white/10 bg-white/[0.04] p-4">
                    <div className="flex items-center justify-between">
                      <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/40">Delivered</p>
                      <p className="font-display text-sm font-bold">XX%</p>
                    </div>
                    <div className="mt-2 h-1.5 bg-white/10"><div className="h-full w-[62%] bg-[#14B8A6]/60" /></div>
                  </div>
                  <div className="col-span-2 flex items-center justify-between border border-white/10 bg-white/[0.04] p-4">
                    <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/40">Logistics</p>
                    <p className="flex items-center gap-2 font-display text-sm font-bold text-[#14B8A6]">
                      <span className="relative flex h-2 w-2"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#14B8A6] opacity-60" /><span className="relative inline-flex h-2 w-2 rounded-full bg-[#14B8A6]" /></span>
                      ON TRACK
                    </p>
                  </div>
                </div>
                <p className="mt-5 text-[11px] text-white/35">Illustrative concept interface. Not live company data.</p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* 10 — FUTURE PLATFORM / MATERIAL BANK */}
      <section className="py-20 sm:py-28" data-testid="future-platform-section">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-3">
            <Reveal>
              <span className="rounded-full border border-[#D97706]/40 bg-[#D97706]/10 px-3.5 py-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-[#D97706]" data-testid="coming-next-badge">
                Coming next
              </span>
              <h2 className="mt-6 font-display text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">
                THE FUTURE OF CARIBBEAN PROCUREMENT
              </h2>
              <p className="mt-5 text-sm leading-relaxed text-[#0B132B]/70 sm:text-base">
                A future BUY CARIBBEAN platform could allow professional clients to manage their entire material strategy from a single interface.
              </p>
            </Reveal>
            <div className="grid gap-4 sm:grid-cols-2 lg:col-span-2">
              {FUTURE_FEATURES.map((f, i) => (
                <Reveal key={f.t} delay={i * 50}>
                  <div className="flex h-full items-center gap-4 border border-[#0B132B]/12 bg-white p-5 card-lift">
                    <f.icon className="h-5 w-5 shrink-0 text-[#0D9488]" strokeWidth={1.7} />
                    <p className="text-sm font-semibold text-[#0B132B]/80">{f.t}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 11 — COLOMBIA → CARIBBEAN MAP */}
      <section className="border-y border-[#0B132B]/10 bg-white py-20 sm:py-28" data-testid="trade-map-section">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal className="mx-auto max-w-3xl text-center">
            <Eyebrow>Colombia → Caribbean</Eyebrow>
            <h2 className="mt-5 font-display text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl" data-testid="trade-map-headline">
              Connecting Colombian supply with Caribbean demand.
            </h2>
            <p className="mt-4 text-sm text-[#0B132B]/65 sm:text-base">
              A regional sourcing and procurement vision built around the Caribbean construction and hospitality economy.
            </p>
          </Reveal>
          <Reveal delay={150} className="mt-14">
            <div className="grid items-center gap-8 lg:grid-cols-[1fr_auto_1.4fr]">
              <div className="border border-[#0B132B]/12 bg-[#0B132B] p-8 text-white" data-testid="map-colombia-node">
                <Anchor className="h-6 w-6 text-[#14B8A6]" strokeWidth={1.6} />
                <p className="mt-4 font-display text-xl font-bold tracking-wide">COLOMBIA</p>
                <p className="mt-1 text-sm text-white/60">Sourcing country</p>
                <div className="mt-5 space-y-2">
                  {["Barranquilla", "Cartagena"].map((h) => (
                    <p key={h} className="flex items-center gap-2 text-sm font-semibold text-white/85">
                      <span className="h-1.5 w-1.5 bg-[#14B8A6]" /> {h}
                    </p>
                  ))}
                </div>
                <p className="mt-5 text-[11px] leading-relaxed text-white/40">
                  Access to Colombian suppliers and logistics corridors.
                </p>
              </div>
              <div className="hidden lg:block">
                <div className="route-dash h-0.5 w-24" />
              </div>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3" data-testid="map-destination-nodes">
                {DESTINATIONS.map((d) => (
                  <div key={d} className="border border-[#0B132B]/12 bg-[#F8F7F4] px-4 py-4 text-center text-[13px] font-semibold text-[#0B132B]/80 card-lift">
                    {d}
                  </div>
                ))}
                <div className="col-span-2 border border-dashed border-[#0B132B]/25 px-4 py-4 text-center text-[13px] font-semibold text-[#0B132B]/50 sm:col-span-1">
                  Other Caribbean markets
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 12 — WHY COLOMBIA */}
      <section className="py-20 sm:py-28" data-testid="why-colombia-section">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
            <Reveal>
              <Eyebrow>Why Colombia</Eyebrow>
              <h2 className="mt-5 font-display text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">
                A strategic sourcing base for the Caribbean.
              </h2>
              <div className="mt-8 overflow-hidden">
                <img src={IMG.port} alt="Container port on Colombia's Caribbean coast" className="aspect-[16/10] w-full object-cover" />
              </div>
            </Reveal>
            <Reveal delay={150}>
              <ul className="space-y-4" data-testid="why-colombia-list">
                {WHY_COLOMBIA.map((w) => (
                  <li key={w} className="flex items-start gap-3.5 border-b border-[#0B132B]/10 pb-4">
                    <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-[#0D9488]" />
                    <span className="text-sm font-medium text-[#0B132B]/85 sm:text-base">{w}</span>
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
        </div>
      </section>

      {/* 13 — MARKET CONTEXT */}
      <section className="border-y border-[#0B132B]/10 bg-white py-14" data-testid="market-context-section">
        <div className="mx-auto max-w-3xl px-5 text-center lg:px-8">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#0B132B]/40">Market Context</p>
            <p className="mt-4 font-display text-lg font-semibold leading-relaxed text-[#0B132B]/85 sm:text-xl">
              "Colombia's construction-material export ecosystem is already connected to Caribbean demand, creating opportunities for a specialized procurement platform."
            </p>
            <p className="mt-3 text-xs text-[#0B132B]/40">Source: ProColombia</p>
          </Reveal>
        </div>
      </section>

      {/* 14 — LONG-TERM VISION */}
      <section className="bg-[#0B132B] py-20 text-white sm:py-28 grain-overlay relative" data-testid="vision-section">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
            <Reveal>
              <Eyebrow dark>The Long-Term Vision</Eyebrow>
              <h2 className="mt-5 font-display text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl" data-testid="vision-headline">
                From sourcing platform to Caribbean Construction OS.
              </h2>
              <p className="mt-5 max-w-lg text-sm leading-relaxed text-white/65 sm:text-base">
                Our ambition is to make construction procurement across the Caribbean simpler, more transparent and more connected.
              </p>
              <Sparkles className="mt-8 h-6 w-6 text-[#D97706]" strokeWidth={1.5} />
            </Reveal>
            <Reveal delay={150}>
              <Flow
                dark
                vertical
                testid="vision-flow"
                items={["Project", "Project Intelligence", "Product Selection", "Colombian Sourcing", "Procurement", "Logistics", "Delivery", "Project Monitoring", "Data", "Better Procurement"]}
              />
            </Reveal>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative overflow-hidden py-24 sm:py-32" data-testid="final-cta-section">
        <img src={IMG.villa} alt="Premium Caribbean villa development" className="hero-img absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-[#0B132B]/85" />
        <div className="relative mx-auto max-w-4xl px-5 text-center lg:px-8">
          <Reveal>
            <h2 className="font-display text-2xl font-bold tracking-tight text-white sm:text-3xl lg:text-4xl" data-testid="final-cta-headline">
              Have a project? Let's build the supply strategy.
            </h2>
            <p className="mx-auto mt-5 max-w-xl text-sm text-white/65 sm:text-base">
              Tell us where you're building. We'll show you where Colombian sourcing creates value.
            </p>
            <div className="mt-9 flex flex-wrap items-center justify-center gap-4">
              <PrimaryBtn to="/contact" testid="final-cta-submit" light>SUBMIT YOUR PROJECT</PrimaryBtn>
              <GhostBtn to="mailto:contactbuycaribbean@gmail.com" testid="final-cta-contact" dark>CONTACT BUY CARIBBEAN</GhostBtn>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
